import { FC } from "react";

import { Box, Stack, useTheme } from "@mui/material";
import NextLink from "next/link";

import { formatProgramTopicChips } from "../helpers/categories";
import {
  cleanText,
  formatAwardRangeCompact,
  formatHistoricalAmount,
  formatShortDate,
  isPastUtcDeadline,
  toUtcDay,
} from "../helpers/formatters";
import { getUnlistedStatesReach, UnlistedStatesReach } from "../helpers/geographyScope";
import { getStateName } from "../helpers/states";

import { LifecycleChip } from "./LifecycleChip";

import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

const iconSx = { width: 14, height: 14, color: "currentColor", flexShrink: 0 } as const;

const MapPinIcon: FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={iconSx}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </Box>
);

const ClockIcon: FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={iconSx}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </Box>
);

interface ProgramCardProps {
  programId: string;
  title: string | null;
  funderName: string | null;
  funderType: string | null;
  category: string | null;
  causes?: string[] | null;
  fundingActivityCategories?: string[] | null;
  assistanceListings?: string[] | null;
  awardFloor: number | null;
  awardCeiling: number | null;
  historicalMedian?: number | null;
  deadline: Date | string | null;
  openDate?: Date | string | null;
  eligibleStates: string[] | null;
  geographyScope: string | null;
  lifecycleStatus?: string | null;
}

const UNLISTED_STATES_LABELS: Record<UnlistedStatesReach, string> = {
  nationwide: "All 50 states",
  international: "International",
  unspecified: "Location not specified",
};

const formatEligibleIn = (eligibleStates: string[] | null, geographyScope: string | null): string => {
  if (!eligibleStates || eligibleStates.length === 0) {
    return UNLISTED_STATES_LABELS[getUnlistedStatesReach(geographyScope)];
  }
  if (eligibleStates.length === 1) {
    return `${getStateName(eligibleStates[0])} only`;
  }
  if (eligibleStates.length <= 4) {
    return eligibleStates.map(getStateName).join(", ");
  }
  return `${eligibleStates.slice(0, 3).map(getStateName).join(", ")} & ${eligibleStates.length - 3} more`;
};

const CauseChip: FC<{ label: string }> = ({ label }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: 1.25,
      py: 0.5,
      borderRadius: 999,
      backgroundColor: "rgba(85,85,231,0.08)",
      whiteSpace: "nowrap",
    }}
  >
    <DashboardTypography variant="caption" sx={{ color: "#4F4FC4", fontWeight: 600 }}>
      {label}
    </DashboardTypography>
  </Box>
);

export const ProgramCard: FC<ProgramCardProps> = ({
  programId,
  title,
  funderName,
  funderType,
  category,
  causes,
  fundingActivityCategories,
  assistanceListings,
  awardFloor,
  awardCeiling,
  historicalMedian,
  deadline,
  openDate,
  eligibleStates,
  geographyScope,
  lifecycleStatus,
}) => {
  const theme = useTheme();

  const funderTypeLabel = funderType ? funderType.replace(/_/g, " ") : null;
  const awardLine = formatAwardRangeCompact(awardFloor, awardCeiling);
  const amountText = awardLine ?? formatHistoricalAmount(historicalMedian) ?? null;
  const hasListedStates = Boolean(eligibleStates?.length);
  const topicChips = formatProgramTopicChips({
    assistanceListings,
    fundingActivityCategories,
    causes,
    category,
  });
  const isPastDeadline = isPastUtcDeadline(deadline);
  const effectiveStatus = isPastDeadline ? "CLOSED" : (lifecycleStatus ?? null);
  const isFutureOpen = openDate !== null && openDate !== undefined && toUtcDay(openDate) > toUtcDay(new Date());
  const isForecasted = !isPastDeadline && (lifecycleStatus ?? "").trim().toLowerCase() === "forecasted";

  const dateLine =
    isPastDeadline && deadline
      ? `Closed ${formatShortDate(deadline)}`
      : isForecasted && isFutureOpen
        ? `Opens ${new Date(openDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })}`
        : deadline
          ? `Closes ${formatShortDate(deadline)}`
          : "Rolling deadline";

  const divider = "1px solid rgba(15,16,36,0.08)";

  return (
    <NextLink href={`/grant-finder/grant-programs/${programId}`} style={{ textDecoration: "none", display: "flex" }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          backgroundColor: theme.palette.common.white,
          border: "1px solid rgba(15,16,36,0.06)",
          borderRadius: "16px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 1px 2px rgba(15,16,36,0.04)",
          transition: "border-color 200ms ease, box-shadow 200ms ease, transform 200ms ease",
          cursor: "pointer",
          "&:hover": {
            borderColor: "rgba(15,16,36,0.12)",
            boxShadow: "0 8px 24px rgba(15,16,36,0.08)",
            transform: "translateY(-2px)",
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{
            backgroundColor: "#FAFAFE",
            borderBottom: divider,
            px: { xs: 2.5, md: 3 },
            py: 1.5,
            minHeight: 44,
          }}
        >
          {funderTypeLabel ? (
            <DashboardTypography
              variant="caption"
              sx={{
                color: "#6F6FB0",
                textTransform: "uppercase",
                letterSpacing: 0.8,
                fontWeight: 700,
                fontSize: 11,
              }}
            >
              {funderTypeLabel}
            </DashboardTypography>
          ) : (
            <Box />
          )}
          <LifecycleChip status={effectiveStatus} />
        </Stack>

        <Stack
          spacing={1.5}
          sx={{
            flex: 1,
            px: { xs: 2.5, md: 3 },
            pt: { xs: 2, md: 2.5 },
            pb: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack spacing={0.5}>
            <DashboardTypography variant="body2" vibe="text-neutral-quiet" sx={{ fontSize: 13 }}>
              {funderName ? cleanText(funderName) : "Unknown funder"}
            </DashboardTypography>
            <DashboardTypography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                fontSize: 16,
                color: theme.palette.text.primary,
                lineHeight: 1.3,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {title ? cleanText(title) : "Untitled program"}
            </DashboardTypography>
          </Stack>

          {topicChips.length > 0 ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {topicChips.map((label) => (
                <CauseChip key={label} label={label} />
              ))}
            </Stack>
          ) : null}

          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: theme.palette.text.secondary }}>
            <MapPinIcon />
            <DashboardTypography
              variant="body2"
              sx={{ fontWeight: hasListedStates ? 500 : 400, fontSize: 13, color: theme.palette.text.primary }}
            >
              {formatEligibleIn(eligibleStates, geographyScope)}
            </DashboardTypography>
          </Stack>

          {amountText ? (
            <Stack spacing={0.25}>
              <DashboardTypography
                variant="subtitle1"
                sx={{ fontWeight: 800, fontSize: 18, color: theme.palette.text.primary, lineHeight: 1.1 }}
              >
                {amountText}
              </DashboardTypography>
            </Stack>
          ) : null}

          <Box sx={{ flex: 1, minHeight: 8 }} />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ borderTop: divider, py: 1.75 }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: theme.palette.text.secondary }}>
              <ClockIcon />
              <DashboardTypography variant="body2" vibe="text-neutral-quiet" sx={{ fontSize: 13 }}>
                {dateLine}
              </DashboardTypography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "#5555E7" }}>
              <DashboardTypography
                variant="body2"
                sx={{ fontWeight: 700, fontSize: 13, color: "#5555E7", whiteSpace: "nowrap" }}
              >
                View details
              </DashboardTypography>
              <Box component="span" sx={{ fontWeight: 700, lineHeight: 1 }}>
                →
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </NextLink>
  );
};
